import { Link } from "react-router-dom";
import { useLang } from "../i18n";
import { useBoot } from "../components/Layout";
import { useSolutions, useSectors, useSiteImages, mergeHome } from "../content/useContent";
import { IconByName } from "../components/IconByName";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { NetworkMap } from "../components/NetworkMap";
import { FinalCTA } from "../components/FinalCTA";
import { ArrowRight, Check } from "lucide-react";

const DEFAULT_ORDER = ["credibility", "mission", "solutionsHome", "network", "segments", "rural", "why", "ecosystem"];

const Home = () => {
  const { lang, t: baseT, p } = useLang();
  const boot = useBoot();
  const t = mergeHome(baseT, boot, lang);
  const IMG = useSiteImages();
  const solutions = useSolutions();
  const sectors = useSectors();
  const claims = boot?.settings?.claims || {};
  const capacity = (lang === "fr" ? claims.ip_transit_capacity_fr : claims.ip_transit_capacity_en) || (lang === "fr" ? "Jusqu'à 100 Gbps" : "Up to 100 Gbps");
  const capacityNote = (lang === "fr" ? claims.ip_transit_capacity_note_fr : claims.ip_transit_capacity_note_en) || "";
  const partners = boot?.partners || [];

  const savedOrder = boot?.settings?.home_order;
  const order = Array.isArray(savedOrder) && savedOrder.length ? savedOrder : DEFAULT_ORDER;

  const blocks = {
    credibility: (
      <section className="bg-[#101214] border-y border-white/10" data-testid="credibility-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {t.credibility.items.map((item, i) => (
            <Reveal key={i} delay={i * 90}>
              <div className="border-l-2 border-[#FF6600] pl-4">
                <p className="text-white font-black text-base sm:text-lg">{item.title.replace("__CAPACITY__", capacity)}</p>
                <p className="mt-1 text-xs sm:text-sm text-white/50">{item.sub.replace("__CAPACITY_NOTE__", capacityNote)}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    ),
    mission: (
      <section className="bg-[#F5F5F3]" data-testid="mission-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-7">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-[#101214]">{t.mission.heading}</h2>
            <p className="mt-6 text-base sm:text-lg text-[#101214]/70 leading-relaxed">{t.mission.copy}</p>
            <div className="mt-8 bg-white border-l-4 border-[#FF6600] rounded-r-2xl p-6 shadow-sm">
              <p className="text-sm sm:text-base font-bold text-[#101214] leading-relaxed">{t.mission.highlight}</p>
            </div>
            <Link to={p("a-propos")} data-testid="mission-cta" className="mt-8 inline-flex items-center gap-2 text-[#FF6600] font-black text-sm hover:gap-3 transition-[gap]">
              {t.mission.cta} <ArrowRight size={16} />
            </Link>
          </Reveal>
          <Reveal className="lg:col-span-5" delay={150}>
            <img src={IMG.teamMeeting} alt={lang === "fr" ? "Équipe Bula Telecoms en réunion" : "Bula Telecoms team meeting"} loading="lazy"
              className="rounded-2xl border border-[#101214]/10 shadow-2xl w-full object-cover aspect-[4/3]" />
          </Reveal>
        </div>
      </section>
    ),
    solutionsHome: (
      <section className="bg-white" data-testid="solutions-overview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-24 lg:py-32">
          <Reveal>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-[#101214] max-w-3xl">{t.solutionsHome.heading}</h2>
            <p className="mt-5 text-base sm:text-lg text-[#101214]/65 leading-relaxed max-w-3xl">{t.solutionsHome.intro}</p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {solutions.slice(0, 6).map((s, i) => (
              <Reveal key={s.slug} delay={i * 70}>
                <Link to={p(`solutions/${s.slug}`)} data-testid={`solution-card-${s.slug}`}
                  className="group block h-full bg-[#F5F5F3] hover:bg-[#101214] border border-[#101214]/10 hover:border-[#FF6600]/40 rounded-2xl p-7 transition-colors duration-300">
                  <span className="inline-flex w-12 h-12 rounded-xl bg-[#FF6600]/12 text-[#FF6600] items-center justify-center group-hover:bg-[#FF6600] group-hover:text-[#08090A] transition-colors">
                    <IconByName name={s.icon} />
                  </span>
                  <h3 className="mt-5 text-lg sm:text-xl font-bold text-[#101214] group-hover:text-white transition-colors">{s[lang].title}</h3>
                  <p className="mt-3 text-sm text-[#101214]/60 group-hover:text-white/60 leading-relaxed transition-colors">{s[lang].short}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-black text-[#FF6600] uppercase tracking-wider">
                    {lang === "fr" ? "Découvrir" : "Discover"} <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-10 text-center">
            <Link to={p("solutions")} data-testid="solutions-explore-cta"
              className="inline-flex items-center gap-2 bg-[#101214] hover:bg-[#FF6600] text-white hover:text-[#08090A] font-black rounded-full px-8 py-3.5 text-sm transition-colors">
              {t.solutionsHome.cta} <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    ),
    network: (
      <section className="bg-[#08090A] relative overflow-hidden" data-testid="network-section">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(#FF6600 1px, transparent 1px)", backgroundSize: "30px 30px" }} aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-white">{t.network.heading}</h2>
            <p className="mt-6 text-base sm:text-lg text-white/65 leading-relaxed">{t.network.copy}</p>
            <ul className="mt-8 space-y-3">
              {t.network.highlights.map((h) => (
                <li key={h} className="flex items-start gap-3 text-sm sm:text-base text-white/75">
                  <Check size={18} className="text-[#FF6600] shrink-0 mt-0.5" /> {h}
                </li>
              ))}
            </ul>
            <Link to={p("infrastructure")} data-testid="network-cta"
              className="mt-9 inline-flex items-center gap-2 border border-[#FF6600]/60 text-[#FF9900] hover:bg-[#FF6600] hover:text-[#08090A] font-black rounded-full px-7 py-3 text-sm transition-colors">
              {t.network.cta} <ArrowRight size={16} />
            </Link>
          </Reveal>
          <Reveal delay={150}>
            <NetworkMap label={t.network.mapLabel} />
          </Reveal>
        </div>
      </section>
    ),
    segments: (
      <section className="bg-[#F5F5F3]" data-testid="segments-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-24 lg:py-32">
          <Reveal><h2 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-[#101214]">{t.segments.heading}</h2></Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {sectors.map((s, i) => (
              <Reveal key={s.slug} delay={i * 60}>
                <Link to={p(`secteurs/${s.slug}`)} data-testid={`sector-card-${s.slug}`}
                  className="group block h-full bg-white border border-[#101214]/10 rounded-2xl p-7 hover:border-[#FF6600]/50 hover:shadow-[0_10px_40px_rgba(255,102,0,0.12)] transition-all duration-300">
                  <IconByName name={s.icon} className="text-[#FF6600]" />
                  <h3 className="mt-4 text-lg font-bold text-[#101214]">{s[lang].title}</h3>
                  <p className="mt-2 text-sm text-[#101214]/60 leading-relaxed">{s[lang].short}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    ),
    rural: (
      <section className="bg-[#101214] relative overflow-hidden" data-testid="rural-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <Reveal className="relative">
            <img src={IMG.ruralSchool} alt={lang === "fr" ? "Salle de classe connectée en zone rurale" : "Connected classroom in a rural area"} loading="lazy"
              className="rounded-2xl border border-white/10 w-full object-cover aspect-[4/3]" />
            <div className="absolute -bottom-5 -right-5 w-40 h-40 rounded-full bg-[#FF6600]/15 blur-2xl" aria-hidden="true" />
          </Reveal>
          <Reveal delay={120}>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-white">{t.rural.heading}</h2>
            <p className="mt-6 text-base sm:text-lg text-white/65 leading-relaxed">{t.rural.copy}</p>
            <p className="mt-5 text-sm sm:text-base font-bold text-[#FFC000] leading-relaxed">{t.rural.support}</p>
            <Link to={p("connectivite-rurale")} data-testid="rural-cta"
              className="mt-8 inline-flex items-center gap-2 bg-[#FF6600] hover:bg-[#FF9900] text-[#08090A] font-black rounded-full px-7 py-3 text-sm transition-colors">
              {t.rural.cta} <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    ),
    why: (
      <section className="bg-white" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-24 lg:py-32">
          <Reveal><h2 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-[#101214]">{t.why.heading}</h2></Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
            {t.why.items.map((w, i) => (
              <Reveal key={w.title} delay={i * 70}>
                <div className="flex gap-4">
                  <span className="font-mono text-[#FF6600] font-bold text-sm mt-1">0{i + 1}</span>
                  <div>
                    <h3 className="text-lg font-bold text-[#101214]">{w.title}</h3>
                    <p className="mt-2 text-sm text-[#101214]/60 leading-relaxed">{w.copy}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    ),
    ecosystem: (
      <section className="bg-[#F5F5F3] border-t border-[#101214]/5" data-testid="ecosystem-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-14 lg:py-20 text-center">
          <p className="text-xs font-mono tracking-widest uppercase text-[#101214]/45">{t.ecosystem.label}</p>
          {partners.length > 0 ? (
            <div className="mt-8 flex flex-wrap justify-center items-center gap-x-10 gap-y-6" data-testid="partner-logos">
              {partners.map((pt) => (
                <span key={pt.id} className="grayscale opacity-60 hover:opacity-100 hover:grayscale-0 transition-all" title={pt.name}>
                  {pt.logo_url
                    ? <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${pt.logo_url}`} alt={pt.name} className="h-8 w-auto object-contain" loading="lazy" />
                    : <span className="text-lg font-black text-[#101214]/60">{pt.name}</span>}
                </span>
              ))}
            </div>
          ) : (
            <p className="mt-6 text-sm text-[#101214]/40" data-testid="partner-pending-note">
              {lang === "fr"
                ? "Les logos de notre écosystème seront publiés après validation par les organisations concernées."
                : "Our ecosystem logos will be published once approved by the organisations concerned."}
            </p>
          )}
        </div>
      </section>
    ),
  };

  return (
    <div data-testid="home-page">
      <Seo title="" path="" />

      {/* ============ HERO (fixed first) ============ */}
      <section className="relative min-h-[92vh] flex items-center bg-[#08090A] overflow-hidden" data-testid="hero-section">
        <img src={IMG.heroEngineer} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" fetchPriority="high" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#08090A] via-[#08090A]/80 to-[#08090A]/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#08090A] via-transparent to-[#08090A]/60" />
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="hero-trail" style={{ top: "22%" }} />
          <div className="hero-trail" style={{ top: "47%", animationDelay: "1.8s" }} />
          <div className="hero-trail" style={{ top: "71%", animationDelay: "3.4s" }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-20 w-full">
          <Reveal>
            <p className="text-xs sm:text-sm font-mono tracking-widest uppercase text-[#FFC000]/90">{t.hero.eyebrow}</p>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight text-white">
              {t.hero.title1}<br />
              <span className="text-[#FF6600]">{t.hero.title2}</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-white/70 leading-relaxed">{t.hero.sub}</p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link to={p("verifier-la-couverture")} data-testid="hero-primary-cta-coverage"
                className="inline-flex items-center gap-2 bg-[#FF6600] hover:bg-[#FF9900] text-[#08090A] font-black rounded-full px-7 py-3.5 text-sm transition-colors shadow-[0_0_28px_rgba(255,102,0,0.45)]">
                {t.nav.coverage} <ArrowRight size={16} />
              </Link>
              <Link to={p("contact")} data-testid="hero-secondary-cta-expert"
                className="inline-flex items-center gap-2 border border-white/25 hover:border-[#FF6600] hover:text-[#FF9900] text-white font-bold rounded-full px-7 py-3.5 text-sm transition-colors">
                {t.nav.talkExpert}
              </Link>
            </div>
            <div className="mt-12 flex flex-wrap gap-2" data-testid="hero-service-strip">
              {t.hero.strip.map((s) => (
                <span key={s} className="text-xs font-bold text-white/70 border border-white/15 rounded-full px-4 py-2 bg-white/5 backdrop-blur-sm">{s}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ REORDERABLE BLOCKS ============ */}
      {order.filter((k) => blocks[k]).map((k) => <div key={k}>{blocks[k]}</div>)}

      <FinalCTA />
    </div>
  );
};

export default Home;
